import { useEffect, useRef, useState, useCallback } from 'react';
import { Api } from './api';
import { toQuery } from './utils';

interface UseRequestConfig<T> {
  useCursor?: boolean;
  skipInitialCursor?: boolean;
  useOffset?: boolean;
  limit?: number;
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
  payload?: Record<string, any>;
  transform?: (result: T) => T;
  language?: string;
  enabled?: boolean;
}

function isAbortError(err: unknown): boolean {
  return !!(err && typeof err === 'object' && 'name' in err && (err as Error).name === 'AbortError');
}

export function useRequest<T extends { data: any[], meta: any }>(
  endpoint: string,
  config: UseRequestConfig<T> = {}
) {
  const {
    onSuccess,
    onError,
    limit,
    payload = {},
    language,
    enabled = true,
    skipInitialCursor = false,
    useCursor = false,
    useOffset = false,
    transform,
  } = config;
  const [data, setData] = useState<T['data']>([]);
  const [meta, setMeta] = useState<T['meta']>({});
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const offset = useRef<number | null>(null);
  const cursor = useRef<string | boolean>(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);
  const hasFetchedRef = useRef(false);
  const mountedRef = useRef(true);

  const payloadRef = useRef(payload);
  payloadRef.current = payload;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const transformRef = useRef(transform);
  transformRef.current = transform;
  const endpointRef = useRef(endpoint);
  endpointRef.current = endpoint;
  const languageRef = useRef(language);
  languageRef.current = language;
  const limitRef = useRef(limit);
  limitRef.current = limit;
  const skipInitialCursorRef = useRef(skipInitialCursor);
  skipInitialCursorRef.current = skipInitialCursor;
  const useCursorRef = useRef(useCursor);
  useCursorRef.current = useCursor;
  const useOffsetRef = useRef(useOffset);
  useOffsetRef.current = useOffset;

  const fetchData = useCallback(async (reset = false) => {
    // Do not abort the previous request — superseded responses are ignored via
    // requestId. Aborting is what shows "(canceled)" in DevTools when filters
    // settle or two catalog lists mount together.
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const requestId = ++requestIdRef.current;

    setLoading(true);
    setError(null);

    const params = {
      ...(useOffsetRef.current
        ? { offset: offset.current !== null ? offset.current + (limitRef.current || 0) : 0 }
        : {}),
      // eslint-disable-next-line no-nested-ternary
      ...(useCursorRef.current &&
        (typeof cursor.current === 'string'
          || (cursor.current === true && !skipInitialCursorRef.current))
        ? { cursor: cursor.current } : {}),
      ...(limitRef.current ? { limit: limitRef.current } : {}),
      ...payloadRef.current,
    };
    const url = `${endpointRef.current}?${toQuery(params)}`;

    try {
      const rawResult = await Api.get<T>(url, true, {
        signal: controller.signal,
        language: languageRef.current,
      });

      // A newer fetchData/reset won — drop this response without aborting it.
      if (!mountedRef.current || requestId !== requestIdRef.current) return;

      const result = transformRef.current ? transformRef.current(rawResult) : rawResult;

      switch (true) {
        case useOffsetRef.current:
          setData((prev) => (reset ? result.data : [...prev, ...result.data]));
          setMeta(result.meta);
          offset.current = result.meta.pagination?.offset ?? 0;
          break;
        case useCursorRef.current:
          setData((prev) => (reset ? result.data : [...prev, ...result.data]));
          setMeta(result.meta);
          cursor.current = result.meta.pagination?.next_cursor || true;
          break;
        default:
          setData(result.data);
          setMeta(result.meta);
          break;
      }
      setLoading(false);
      onSuccessRef.current?.(result);
    } catch (err: unknown) {
      if (!mountedRef.current || requestId !== requestIdRef.current || isAbortError(err)) {
        return;
      }
      const newErr = err instanceof Error
        ? err
        : (err && typeof err === 'object' ? err : new Error(String(err)));
      setError(newErr as Error);
      onErrorRef.current?.(newErr as Error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (enabled && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchData();
    } else if (!enabled) {
      hasFetchedRef.current = false;
      setLoading(false);
    }
  }, [enabled, fetchData]);

  const reset = useCallback((options?: { skipFetch?: boolean }) => {
    if (!enabled) return;
    setData([]);
    cursor.current = true;
    offset.current = null;
    if (!options?.skipFetch) {
      fetchData(true);
    }
  }, [enabled, fetchData]);

  const syncCursorAndMeta = useCallback((nextCursor: string | boolean, nextMeta: T['meta']) => {
    cursor.current = nextCursor;
    setMeta(nextMeta);
  }, []);

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    setLoading(false);
  }, []);

  return {
    data,
    setData,
    meta,
    error,
    loading,
    refetch: async () => {
      if (!enabled) return Promise.resolve();
      await fetchData(false);
    },
    reset,
    abort,
    syncCursorAndMeta,
  };
}
